import type { StoreProduct, StoreProductVariant } from "@/platform/api/store-contract"
import { isPreviewProduct } from "@/platform/catalog/products.client"

export type SelectedOptions = Record<string, string>

type OptionLookup = (key: string) => string | null | undefined

// Preview products carry no inventory and are still presented as purchasable, so every
// stock check on this page takes the flag rather than reading the quantity alone.
export const isVariantInStock = (variant: StoreProductVariant, isPreview: boolean = false): boolean =>
  isPreview || (variant.inventory_quantity ?? 0) > 0 || Boolean(variant.allow_backorder)

export const hasAnyVariantInStock = (variants: StoreProductVariant[], isPreview: boolean = false): boolean => variants.some((variant) => isVariantInStock(variant, isPreview))

export const isLastUnit = (variant: StoreProductVariant | undefined): boolean => variant?.inventory_quantity === 1 && !variant.allow_backorder

const shallowRecordEquals = (a: SelectedOptions, b: SelectedOptions): boolean => {
  const aKeys = Object.keys(a)
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key])
}

const variantOptionsRecord = (variant: StoreProductVariant): SelectedOptions => {
  const record: SelectedOptions = {}
  for (const option of variant.options ?? []) record[option.option_id] = option.value
  return record
}

// The exact variant whose option set matches the selection completely (both ways).
export const matchVariant = (variants: StoreProductVariant[], options: SelectedOptions): StoreProductVariant | undefined => {
  let match: StoreProductVariant | undefined
  for (const variant of variants) {
    if (variant.id && shallowRecordEquals(variantOptionsRecord(variant), options)) match = variant
  }
  return match
}

/**
 * Whether selecting `value` for `optionId` can still lead to an in-stock variant,
 * holding every other already-selected option fixed. Options the user hasn't picked
 * yet are ignored, so early picks only need SOME compatible in-stock variant.
 */
export const isOptionInStock = (variants: StoreProductVariant[], selectedOptions: SelectedOptions, optionId: string, value: string, isPreview: boolean = false): boolean => {
  const candidate: SelectedOptions = { ...selectedOptions, [optionId]: value }

  return variants.some((variant) => {
    if (!variant.options) return false
    const matches = variant.options.every((variantOption) => {
      const selected = candidate[variantOption.option_id]
      return selected === undefined || selected === variantOption.value
    })
    return matches && isVariantInStock(variant, isPreview)
  })
}

/**
 * Initial option selection: URL params win when valid, single-variant products
 * auto-select, and options with one distinct value pre-fill when an in-stock variant
 * carries it — unless the fully-derived combination is out of stock, in which case
 * nothing is preselected. Ported unchanged from the legacy ProductActions so deep
 * links (?color=Negro&talla=26) keep resolving identically.
 */
export const deriveInitialOptions = (product: StoreProduct, getParam: OptionLookup): SelectedOptions => {
  const variants = product.variants
  const isPreview = isPreviewProduct(product)

  const soleVariant = variants.length === 1 ? variants[0] : undefined
  if (soleVariant?.id && soleVariant.options) {
    return variantOptionsRecord(soleVariant)
  }

  const optionObj: Record<string, string | undefined> = {}
  for (const option of product.options ?? []) {
    const urlParamValue = getParam(option.title.toLowerCase())
    if (urlParamValue && option.values.some((optionValue) => optionValue.value === urlParamValue)) {
      optionObj[option.id] = urlParamValue
      continue
    }
    const uniqueValues = Array.from(new Set(option.values.map((optionValue) => optionValue.value)))
    if (uniqueValues.length === 1) {
      const singleValue = uniqueValues[0]
      const hasInStockVariant = variants.some((variant) => {
        const variantOption = variant.options?.find((vo) => vo.option_id === option.id)
        return variantOption?.value === singleValue && isVariantInStock(variant, isPreview)
      })
      if (hasInStockVariant) optionObj[option.id] = singleValue
    }
  }

  const allOptions = product.options ?? []
  const hasAll = allOptions.length > 0 && allOptions.every((option) => optionObj[option.id] !== undefined)
  if (hasAll) {
    let matched: StoreProductVariant | undefined
    for (const variant of variants) {
      if (variant.options?.every((vo) => optionObj[vo.option_id] === vo.value)) matched = variant
    }
    if (matched && !isVariantInStock(matched, isPreview)) return {}
  }

  const result: SelectedOptions = {}
  for (const [key, value] of Object.entries(optionObj)) if (value !== undefined) result[key] = value
  return result
}

// Option-id → lowercased title map, used to mirror the selection into URL params.
export const optionIdToUrlKey = (product: StoreProduct): Record<string, string> => {
  const map: Record<string, string> = {}
  for (const option of product.options ?? []) map[option.id] = option.title.toLowerCase()
  return map
}
